import { useAuth } from "@clerk/expo";
import { ImagePickerAsset } from "expo-image-picker";
import { useIsFocused, useRouter } from "expo-router";
import { ImagePlus, Users } from "lucide-react-native";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, ScrollView } from "react-native";

import {
  Button,
  ButtonSpinner,
  ButtonText,
  Card,
  Center,
  DeleteDialog,
  HStack,
  MediaPicker,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { useFileUpload } from "@/hooks/storage";
import { Panel, Study } from "@/types/resources";
import { getUseCaseSlug } from "@/types/utils";

import PanelMentionInput, { findMentionedPanels } from "./PanelMentionInput";
import StudyDesignPreview from "./StudyDesignPreview";

/** Height of the attachment trigger and of every thumbnail next to it. */
const ATTACHMENT_SIZE = 42;

/** Images the brief can be built around — mirrors `SuggestStudyRequest.media`. */
const MAX_ATTACHMENTS = 10;

/** Characters the brief must have once the `@Name` mentions are removed. */
const MIN_BRIEF_LENGTH = 20;

interface StudyPromptProps {
  /** Called with the kept study when the user confirms "Create study". */
  onStudyCreated?: (study: Study) => void;
}

/**
 * Home-page card: describe a study in plain language, tag the audiences
 * (saved panels) with `@`, and get a full study designed and imported as a
 * draft to review, edit and keep — or discard.
 */
const StudyPrompt: FC<StudyPromptProps> = ({ onStudyCreated }) => {
  const { i18n } = useLocalization();
  const { getToken } = useAuth();
  const isFocused = useIsFocused();
  const router = useRouter();
  const [prompt, setPrompt] = useState("");
  const [attachments, setAttachments] = useState<ImagePickerAsset[]>([]);
  const [knownPanels, setKnownPanels] = useState<Panel[]>([]);
  const [draft, setDraft] = useState<Study | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  /* Uploaded key of each attachment, by asset uri. Regenerating or editing
     the brief resends those paths instead of uploading again; an attachment
     removed after upload just stays in `temp/` until S3 expires it. */
  const [uploadedKeys, setUploadedKeys] = useState<Record<string, string>>({});

  const { uploadFile } = useFileUpload();

  const {
    error: suggestError,
    loading: suggesting,
    suggestStudy,
  } = API.AI.useSuggestStudy();
  const {
    error: importError,
    importStudy,
    loading: importing,
  } = API.Studies.useImportStudy();
  const {
    deleteStudy,
    error: deleteError,
    loading: deleting,
  } = API.Studies.useDeleteStudy();

  /* Panels picked from the dropdown stay "mentioned" only while their
     `@Name` is still in the text — deleting the mention drops the chip. */
  const mentionedPanels = useMemo(
    () => findMentionedPanels(prompt, knownPanels),
    [knownPanels, prompt]
  );

  const addPanel = useCallback((panel: Panel) => {
    setKnownPanels((prev) =>
      prev.some((p) => p.id === panel.id) ? prev : [...prev, panel]
    );
  }, []);

  /** Upload the attachments that have no key yet, and return every
      attachment's key in order. They go under a prefix that expires, since
      no study exists yet — the import copies the ones the design keeps into
      the study's own folder. */
  const uploadAttachments = useCallback(async (): Promise<string[]> => {
    const pending = attachments.filter(({ uri }) => !uploadedKeys[uri]);
    if (pending.length === 0) {
      return attachments.map(({ uri }) => uploadedKeys[uri]);
    }

    const progresses: number[] = new Array(pending.length).fill(0);
    // Non-zero from the start so the button reads "Uploading…" right away.
    setUploadProgress(Number.MIN_VALUE);
    try {
      const uploaded = await Promise.all(
        pending.map(async (file, index) => {
          const key = await uploadFile({
            file,
            folder: "temp",
            onProgress: (progress) => {
              progresses[index] = progress;
              setUploadProgress(
                progresses.reduce((sum, p) => sum + p, 0) / pending.length
              );
            },
          });
          return [file.uri, key] as const;
        })
      );
      const keys = { ...uploadedKeys, ...Object.fromEntries(uploaded) };
      setUploadedKeys(keys);
      return attachments.map(({ uri }) => keys[uri]);
    } finally {
      setUploadProgress(0);
    }
  }, [attachments, uploadFile, uploadedKeys]);

  /** Ask for a design and import it right away as a draft study. Aborted
      (the user left) → the suggestion is dropped, or the study just imported
      is deleted, and null is returned. */
  const design = useCallback(async (): Promise<Study | null> => {
    const controller = new AbortController();
    abortRef.current = controller;
    const media = await uploadAttachments();
    if (controller.signal.aborted) return null;
    const suggestion = await suggestStudy(
      {
        media,
        panel_ids: mentionedPanels.map((p) => p.id),
        prompt: prompt.trim(),
      },
      controller.signal
    );
    if (!suggestion || controller.signal.aborted) return null;
    const study = await importStudy(suggestion.import_request);
    if (study && controller.signal.aborted) {
      await deleteStudy(study.id);
      return null;
    }
    return study;
  }, [
    deleteStudy,
    importStudy,
    mentionedPanels,
    prompt,
    suggestStudy,
    uploadAttachments,
  ]);

  const handleDesign = useCallback(async () => {
    const study = await design();
    if (!study) return;
    setDraft(study);
    setPreviewOpen(true);
  }, [design]);

  /* Design the replacement first so a failed attempt keeps the current draft
     open (with the error) instead of losing it. */
  const handleRegenerate = useCallback(async () => {
    if (!draft) return;
    setRegenerating(true);
    try {
      const study = await design();
      if (!study) return;
      await deleteStudy(draft.id);
      setDraft(study);
    } finally {
      setRegenerating(false);
    }
  }, [deleteStudy, design, draft]);

  const handleCancel = useCallback(async () => {
    if (!draft) return;
    abortRef.current?.abort();
    const deleted = await deleteStudy(draft.id);
    if (!deleted) return;
    setDraft(null);
    setPreviewOpen(false);
  }, [deleteStudy, draft]);

  const handleCreate = useCallback(() => {
    if (!draft) return;
    const study = draft;
    setPreviewOpen(false);
    setDraft(null);
    setPrompt("");
    setAttachments([]);
    setKnownPanels([]);
    setUploadedKeys({});
    onStudyCreated?.(study);
    router.push(
      study.use_case
        ? `/(dashboard)/quick-insights/${getUseCaseSlug(study.use_case)}/${study.id}?tab=audience`
        : `/(dashboard)/studies/${study.id}`
    );
  }, [draft, onStudyCreated, router]);

  /* Refresh / close tab: the browser only allows its native prompt. If the
     user confirms, the page is gone before axios could run, so the draft is
     deleted with a keepalive request that outlives the page. */
  useEffect(() => {
    if (Platform.OS !== "web" || !draft) return;

    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return "";
    };
    const discard = () => {
      getToken().then((token) =>
        fetch(`${process.env.EXPO_PUBLIC_API_URL}/studies/${draft.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true,
          method: "DELETE",
        })
      );
    };

    window.addEventListener("beforeunload", warn);
    window.addEventListener("pagehide", discard);
    return () => {
      window.removeEventListener("beforeunload", warn);
      window.removeEventListener("pagehide", discard);
    };
  }, [draft, getToken]);

  /* Browser back / forward: Home is a drawer screen that stays mounted, so
     the draft survives the navigation. Ask before letting it go. */
  const leaving = draft !== null && !isFocused;
  const handleStay = useCallback(
    () => router.navigate("/(dashboard)"),
    [router]
  );

  const uploading = uploadProgress > 0;
  const pendingUploads = attachments.filter(
    ({ uri }) => !uploadedKeys[uri]
  ).length;
  const loading = uploading || suggesting || importing;
  const busy = loading || deleting;
  const error = suggestError ?? importError ?? deleteError;
  /* Tagging segments alone is not a brief — measure what's left without the
     mentions. */
  const briefLength = mentionedPanels
    .reduce((text, panel) => text.split(`@${panel.name}`).join(""), prompt)
    .trim().length;
  const canDesign =
    briefLength >= MIN_BRIEF_LENGTH && mentionedPanels.length > 0 && !busy;

  return (
    <>
      <Card className="border border-outline-100 rounded-2xl p-5 gap-4">
        <MediaPicker
          isDisabled={busy}
          maxItems={MAX_ATTACHMENTS}
          media={attachments}
          mediaType="images"
          onMediaChange={(media) => setAttachments(media as ImagePickerAsset[])}
        >
          <VStack>
            <Text className="text-base font-semibold text-typography-900">
              {i18n.t("studies.design.title")}
            </Text>
            <Text className="text-sm text-typography-500">
              {i18n.t("studies.design.subtitle")}
            </Text>
          </VStack>

          {/* Attachments live inside the input, under the brief they
              illustrate: the ads, packs or concepts to design around. */}
          <PanelMentionInput
            footer={
              <HStack className="flex-1 items-center gap-1">
                <MediaPicker.Trigger>
                  <Center
                    className="rounded-lg border border-dashed border-outline-300 hover:bg-background-200"
                    style={{
                      height: ATTACHMENT_SIZE,
                      width: ATTACHMENT_SIZE,
                    }}
                  >
                    <ImagePlus
                      color="rgb(var(--color-typography-500))"
                      size={20}
                    />
                  </Center>
                </MediaPicker.Trigger>
                {/* Scrolls sideways: the bar's height must not move. */}
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  /* Room for the delete badge, which sits 8px outside the
                     thumbnail and would be clipped by the scroll box. */
                  contentContainerStyle={{ padding: 10 }}
                >
                  <MediaPicker.Thumbnails gap={6} height={ATTACHMENT_SIZE} />
                </ScrollView>
              </HStack>
            }
            isDisabled={busy}
            onChangeText={setPrompt}
            onSelectPanel={addPanel}
            placeholder={i18n.t("studies.design.placeholder")}
            selectedPanels={mentionedPanels}
            value={prompt}
          />
          <MediaPicker.Error />

          <HStack className="items-center justify-between gap-3">
            <HStack className="flex-1 flex-wrap items-center gap-2">
              {mentionedPanels.map((panel) => (
                <HStack
                  className="items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 border border-primary-100"
                  key={panel.id}
                >
                  <Users color="rgb(var(--color-primary-700))" size={12} />
                  <Text className="text-sm font-medium text-primary-700">
                    {panel.name}
                  </Text>
                </HStack>
              ))}
            </HStack>
            <Button
              className="px-6 py-3 rounded-lg bg-primary-500"
              isDisabled={!canDesign}
              onPress={handleDesign}
            >
              {(loading || uploading) && <ButtonSpinner color="white" />}
              <ButtonText className="text-white">
                {uploading
                  ? i18n.t("misc.uploadingMedia", {
                      count: pendingUploads,
                      percent: i18n.numberToPercentage(uploadProgress * 100, {
                        precision: 0,
                      }),
                    })
                  : loading
                    ? i18n.t("studies.design.designing")
                    : i18n.t("studies.design.designStudy")}
              </ButtonText>
            </Button>
          </HStack>
          {!previewOpen && error && (
            <Text className="text-sm text-error-600 text-right">{error}</Text>
          )}
        </MediaPicker>
      </Card>

      {draft && (
        <StudyDesignPreview
          busy={busy}
          error={error}
          isOpen={previewOpen && isFocused}
          onCancel={handleCancel}
          onCreate={handleCreate}
          onRegenerate={handleRegenerate}
          regenerating={regenerating}
          study={draft}
        />
      )}

      <DeleteDialog
        body={i18n.t("studies.design.leaveBody")}
        deleteButtonText={i18n.t("studies.design.leave")}
        error={deleteError}
        isOpen={leaving}
        loading={deleting}
        onCancel={handleStay}
        onDelete={handleCancel}
        title={i18n.t("studies.design.leaveTitle")}
      />
    </>
  );
};

export default StudyPrompt;
